import { cx } from '@emotion/css';
import { Col } from 'antd';
import { isUndefined } from 'lodash';
import { useSelector } from 'react-redux';
import { OrderTypeDto } from 'src/dtos';
import { useSubmittedAccountSelector } from 'src/hooks';
import {
  CurrencyField,
  MButton,
  MFormDateField,
  MFormTextField,
  MModalBody,
  MModalFooter,
  MModalTitle,
  MTooltip,
  MTypography,
  SubmissionResult,
} from 'src/lib';
import { Order, Quote } from 'src/models';
import { BaseStyle, Spacing } from 'src/styles';
import { findEstimatedTotal, getInvestorName, Maybe } from 'src/utils';

interface SecurityHoldingTradeSucceededStepProps {
  quote: Quote;
  quantity?: number;
  onLastPrice: () => void;
  onServiceFee: () => void;
  onClose: () => void;
}

export const SecurityHoldingTradeSucceededStep = ({
  quote,
  quantity,
  onLastPrice,
  onServiceFee,
  onClose,
}: SecurityHoldingTradeSucceededStepProps) => {
  const order: Maybe<Order> = useSelector((state: any) => state.orders.create.data);
  const { account } = useSubmittedAccountSelector();

  const physicalAddress = [
    account.primaryAccountHolder.physicalAddress.address1,
    account.primaryAccountHolder.physicalAddress.address2,
    account.primaryAccountHolder.physicalAddress.city,
    account.primaryAccountHolder.physicalAddress.postalCode,
    account.primaryAccountHolder.physicalAddress.country.value,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <MModalTitle>Security Order Succeeded</MModalTitle>
      <MModalBody>
        <Col span={24} className={cx(BaseStyle.level, Spacing.my12)}>
          <SubmissionResult type='success' />
        </Col>
        <Col span={24} className={cx(BaseStyle.level, Spacing.my24)}>
          <MTypography variant='h6' align='center'>
            {quote?.symbol}
          </MTypography>
        </Col>
        <Col span={24}>
          <MFormTextField label='Investor Name' value={getInvestorName(account)} />
        </Col>
        <Col span={24}>
          <MFormTextField label='Address' value={physicalAddress} />
        </Col>
        <Col span={24}>
          <MFormDateField label='Date' value={order?.updatedAt} />
        </Col>
        <Col span={24}>
          <MFormTextField label='Order Type' value={order?.type?.label} />
        </Col>
        <Col span={24}>
          <MFormTextField
            label={
              <>
                Last Price
                <MTooltip showIcon onClick={() => onLastPrice()} />
              </>
            }
            value={<CurrencyField value={quote.last} />}
          />
        </Col>
        {!isUndefined(quantity) && (
          <Col span={24}>
            <MFormTextField label='Shares Held' value={quantity} />
          </Col>
        )}
        {order?.type?.value === OrderTypeDto.Limit || order?.type?.value === OrderTypeDto.StopLimit ? (
          <Col span={24}>
            <MFormTextField label='Limit Price' value={<CurrencyField value={order.price} />} />
          </Col>
        ) : null}
        {order?.type?.value === OrderTypeDto.StopLimit || order?.type?.value === OrderTypeDto.StopMarket ? (
          <Col span={24}>
            <MFormTextField label='Stop Price' value={<CurrencyField value={order.stop} />} />
          </Col>
        ) : null}
        <Col span={24}>
          <MFormTextField label={`Shares to ${order?.action?.label}`} value={order?.quantity} />
        </Col>
        <Col span={24}>
          <MFormTextField label='Order Expiration' value={order?.duration?.label} />
        </Col>
        <Col span={24}>
          <MFormTextField
            label={
              <>
                Service Fee
                <MTooltip showIcon onClick={() => onServiceFee()} />
              </>
            }
            value='$10.00'
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Estimated Total'
            value={
              <CurrencyField
                value={findEstimatedTotal({
                  action: order?.action?.value,
                  type: order?.type.value,
                  quantity: order?.quantity,
                  price: order?.price,
                  stop: order?.stop,
                  value: quote.last,
                })}
              />
            }
          />
        </Col>
        <Col span={24}></Col>
      </MModalBody>
      <MModalFooter className={BaseStyle.levelRight}>
        <MButton type='tertiary' onClick={onClose}>
          Close
        </MButton>
      </MModalFooter>
    </>
  );
};
